/* ===========================================================================
   Activation Products — UGC Testimonials
   Adapted from the vendor widget supplied by Sascha (ap-ugc-widget.js).

   WHAT CHANGED FROM THE VENDOR BUILD, AND WHY
   ---------------------------------------------------------------------------
   1. Endpoint. The vendor widget called `/api/widget`, which returns 404. The
      only live route is `/api/testimonials`.

   2. Server-side params are ignored. `/api/testimonials` returns the full set
      (111 entries) regardless of `type` or `limit`, so both are applied here
      on the client.

   3. PII. The API returns each customer's `email` and full `name`. This module
      never copies `email` onto the view model and shortens `name` to
      "First L." before render.

      READ THIS: that is a render-level guard, NOT a privacy fix. The raw
      response — emails included — still travels to every visitor's browser and
      is readable in DevTools or by hitting the endpoint directly. The real fix
      is a sanitized endpoint on the portal side. Tracked as an ask to Sascha.

   4. Ratings. The vendor build hardcoded a 5-star rating on every card. The API
      has no rating field, so those stars represented nothing. Rendering
      invented ratings for a supplement brand is a misleading-advertising
      exposure (Competition Act, and FTC for US traffic), so they are omitted.
      The heading's aggregate rating is a separate merchant-entered claim.
      If a real `rating` field is added upstream, render it here.

   5. Incentivized content. Entries were collected via a contest that paid
      customers in store credit. That is a material connection, so the section
      renders a disclosure by default.

   6. Video weight. Media ranges from 2.9 MB to 172.5 MB. The vendor build used
      `preload="metadata"` on every video; here videos render as a facade
      (poster + play button) and the file is only requested on click.

   7. Video format. Media is a mix of MP4, QuickTime and WebM/VP9. WebM/VP9
      does not play in Safari. The JSON carries no mimetype, so when the
      browser lacks WebM support we HEAD the visible videos and drop the ones
      it cannot play, instead of rendering a dead player.

   LAYOUT
   ---------------------------------------------------------------------------
   The masonry grid and its "Show more" button are gone — the design is a
   carousel, built on the theme's global Swiper bundle rather than the previous
   hand-rolled scroll-snap, per theme convention.

   Cards come in two shapes. Entries with media render the Figma card (media
   full-bleed, play button, name over a blurred bar). Text-only reviews render
   a cream card with the quote and the same name block; Figma has no treatment
   for those, so it is derived from the same tokens.
   =========================================================================== */
(function () {
  'use strict';

  var DEFAULTS = {
    endpoint: 'https://knowing-seal-683.convex.site',
    type: 'all', // review | selfie | video | all
    limit: 12,
    demote: '', // entry ids to send to the end, comma or space separated
  };

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /**
   * "Jane Doe" -> "Jane D." — the API sends full names, we never render them.
   */
  function shortenName(name) {
    var parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    // Some portal entries carry a parenthesised nickname ("P. Herrera (Pancho)").
    // Left in, it becomes the last token and the card renders "P. (." — the
    // opening bracket read as a surname initial. Drop those tokens; what
    // remains keeps the first-name-plus-initial shape ("P. H.") rather than
    // falling through to a bare surname, which would expose more than the rest
    // of the library does, not less.
    parts = parts.filter(function (part) {
      return part.charAt(0) !== '(' && part.indexOf(')') === -1;
    });
    // A couple of entries carry placeholder names ("X"). Return empty rather
    // than a stand-in: the card drops the name line and keeps the badge, which
    // beats printing "Verified customer" twice in the same block.
    if (!parts.length || parts.join('').length < 2) return '';
    // Customers type their own names and a handful arrive lowercase ("suzanne").
    // Only the first character is touched, so "McRae" and names the customer
    // capitalised their own way survive intact.
    var first = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    if (parts.length === 1) return first;
    return first + ' ' + parts[parts.length - 1].charAt(0).toUpperCase() + '.';
  }

  /**
   * Build the view model. `email` is deliberately not carried over — if a field
   * is not on this object it cannot reach the DOM.
   */
  function toViewModel(entry) {
    return {
      id: entry.id,
      type: entry.type,
      text: entry.text || '',
      mediaUrl: entry.mediaUrl || '',
      name: shortenName(entry.name),
    };
  }

  var RICHNESS = { video: 3, selfie: 2, review: 1 };

  /* ------------------------------------------------------------------ icons */

  /**
   * SVGs are rendered once by icon.liquid into a hidden block in the section,
   * so the theme keeps a single icon source even though cards are built here.
   */
  function iconHTML(root, name) {
    var node = root.querySelector('[data-ugc-icon="' + name + '"]');
    return node ? node.innerHTML : '';
  }

  /* ---------------------------------------------------------------- posters */

  var posters = null;

  /**
   * Poster frames live in a JSON block rendered by the section, keyed by the
   * storage id at the end of each mediaUrl. Videos with no entry simply get no
   * poster — the play button still works.
   */
  function loadPosters(root) {
    if (posters) return posters;
    posters = {};
    var node = root.querySelector('[data-ugc-posters]');
    if (!node) return posters;
    try {
      posters = JSON.parse(node.textContent) || {};
    } catch (error) {
      if (window.console) console.warn('[c-ugc-testimonials] bad poster map', error);
    }
    return posters;
  }

  function posterFor(mediaUrl) {
    if (!posters) return '';
    var id = String(mediaUrl).split('/').pop().split('?')[0];
    return posters[id] || '';
  }

  /* ------------------------------------------------------------- selection */

  /**
   * 111 entries come from 66 people — the contest paid separately for a review,
   * a selfie and a video, so 16 customers appear three times. Showing the same
   * person repeatedly weakens the social proof, so in "all" mode we keep one
   * piece per person (richest media wins).
   *
   * `email` is used here purely as an in-memory grouping key and is never
   * carried into the view model.
   */
  function onePerPerson(entries) {
    var best = {};
    entries.forEach(function (entry) {
      var key = String(entry.email || entry.id).toLowerCase();
      var current = best[key];
      if (!current || (RICHNESS[entry.type] || 0) > (RICHNESS[current.type] || 0)) {
        best[key] = entry;
      }
    });
    return entries.filter(function (entry) {
      return best[String(entry.email || entry.id).toLowerCase()] === entry;
    });
  }

  /**
   * Lead with media. Text-only reviews are the majority of the library, so the
   * natural API order buries the photos and videos — which are the pieces that
   * actually sell, and the only ones the designed card was drawn for. Stable
   * sort, so within each type the original order holds.
   */
  function mediaFirst(items) {
    return items
      .map(function (item, index) {
        return { item: item, index: index };
      })
      .sort(function (a, b) {
        var diff = (RICHNESS[b.item.type] || 0) - (RICHNESS[a.item.type] || 0);
        return diff !== 0 ? diff : a.index - b.index;
      })
      .map(function (entry) {
        return entry.item;
      });
  }

  /**
   * Open on media, then mix the text reviews back in.
   *
   * mediaFirst alone starves the text card: after one-per-person the library
   * splits ~31 media / ~36 text-only, so a strict media-first order fills every
   * slide within the default limit with media and a text review is never seen.
   * Lead with a full screen of media (the designed card, and what sells), then
   * deal two media to one review so both card types ride the same carousel.
   */
  var LEAD_MEDIA = 4; // a full row at the widest breakpoint
  var MEDIA_PER_TEXT = 2;

  function interleave(items, demoteIds) {
    // Demoted entries are pulled before the rhythm is dealt, not after. Removing
    // one from the middle of a finished order just shifts everything up a slot,
    // which is how a demoted video ends up replaced by a text card instead of
    // by the next video.
    var demoted = [];
    var pool = [];
    items.forEach(function (item) {
      if (demoteIds.indexOf(item.id) !== -1) demoted.push(item);
      else pool.push(item);
    });

    var media = pool.filter(function (item) {
      return !!item.mediaUrl;
    });
    var text = pool.filter(function (item) {
      return !item.mediaUrl;
    });

    var out = media.splice(0, LEAD_MEDIA);
    while (media.length || text.length) {
      for (var i = 0; i < MEDIA_PER_TEXT && media.length; i++) out.push(media.shift());
      if (text.length) out.push(text.shift());
    }
    return out.concat(demoted);
  }

  /**
   * Entries a merchant has sent to the end, by API id.
   *
   * The reorder is applied to the visible window, not the whole library:
   * moving an id to the end of all 67 entries would push it past the limit and
   * drop it from the carousel, which is a removal wearing a reorder's clothes.
   * Demoted cards still count against the limit, so the slide count holds.
   */
  function parseIds(raw) {
    return String(raw || '')
      .split(/[\s,]+/)
      .filter(Boolean);
  }

  function takeSlides(items, limit, demoteIds) {
    if (!demoteIds.length) return items.slice(0, limit);

    var isDemoted = function (item) {
      return demoteIds.indexOf(item.id) !== -1;
    };
    var demoted = items.filter(isDemoted);
    var rest = items.filter(function (item) {
      return !isDemoted(item);
    });

    return rest.slice(0, Math.max(0, limit - demoted.length)).concat(demoted.slice(0, limit));
  }

  function supportsWebm() {
    var probe = document.createElement('video');
    return !!probe.canPlayType && probe.canPlayType('video/webm; codecs="vp9"') !== '';
  }

  /**
   * The payload has no mimetype and the storage URLs have no extension, so the
   * only way to know a format is to ask. Only runs when the browser cannot play
   * WebM — on Chrome/Firefox this costs nothing.
   */
  function dropUnplayableVideos(items) {
    if (supportsWebm()) return Promise.resolve(items);

    var checks = items.map(function (item) {
      if (item.type !== 'video' || !item.mediaUrl) return Promise.resolve(item);
      return fetch(item.mediaUrl, { method: 'HEAD' })
        .then(function (res) {
          var contentType = (res.headers.get('content-type') || '').toLowerCase();
          return contentType.indexOf('webm') === -1 ? item : null;
        })
        .catch(function () {
          return item; // network hiccup: keep it and let the player decide
        });
    });

    return Promise.all(checks).then(function (checked) {
      return checked.filter(Boolean);
    });
  }

  /* ------------------------------------------------------------------ cards */

  function infoHTML(root, item) {
    var name = item.name
      ? '<p class="c-ugc-testimonials__name">' + esc(item.name) + '</p>'
      : '';

    return (
      '<div class="c-ugc-testimonials__info">' +
      name +
      '<span class="c-ugc-testimonials__verified">' +
      iconHTML(root, 'verified') +
      '<span>Verified customer</span>' +
      '</span>' +
      '</div>'
    );
  }

  /**
   * The designed card: media fills the slide, play button and name bar sit on
   * top of it.
   */
  function mediaCardHTML(root, item) {
    var isVideo = item.type === 'video';
    var image = isVideo ? posterFor(item.mediaUrl) : item.mediaUrl;
    // Entries with a placeholder name render no name; screen readers still
    // need something to attribute the media to.
    var who = item.name ? esc(item.name) : 'a verified customer';

    var mediaImg = image
      ? '<img class="c-ugc-testimonials__media" loading="lazy" decoding="async" src="' +
        esc(image) +
        '" alt="' +
        (isVideo ? '' : 'Photo submitted by ' + who) +
        '">'
      : '';

    var play = isVideo
      ? '<button type="button" class="c-ugc-testimonials__play" data-video-src="' +
        esc(item.mediaUrl) +
        '"' +
        (image ? ' data-poster="' + esc(image) + '"' : '') +
        ' aria-label="Play video testimonial from ' +
        who +
        '">' +
        iconHTML(root, 'play') +
        '</button>'
      : '';

    return (
      '<article class="c-ugc-testimonials__card c-ugc-testimonials__card--media">' +
      mediaImg +
      play +
      infoHTML(root, item) +
      '</article>'
    );
  }

  /**
   * Text-only reviews. Same footprint and name block, cream card, no invented
   * star rating.
   */
  function textCardHTML(root, item) {
    return (
      '<article class="c-ugc-testimonials__card c-ugc-testimonials__card--text">' +
      '<p class="c-ugc-testimonials__quote">' +
      esc(item.text) +
      '</p>' +
      infoHTML(root, item) +
      '</article>'
    );
  }

  function slideHTML(root, item) {
    var card = item.mediaUrl ? mediaCardHTML(root, item) : textCardHTML(root, item);
    return '<div class="c-ugc-testimonials__slide swiper-slide">' + card + '</div>';
  }

  function skeletonsHTML(count) {
    var out = '';
    for (var i = 0; i < count; i++) {
      out +=
        '<div class="c-ugc-testimonials__slide swiper-slide">' +
        '<div class="c-ugc-testimonials__skeleton"></div>' +
        '</div>';
    }
    return out;
  }

  /**
   * Swap the facade for a real player. Nothing is fetched until this runs.
   */
  function activateVideo(button) {
    var card = button.closest('.c-ugc-testimonials__card');
    if (!card) return;

    var src = button.getAttribute('data-video-src');
    var poster = button.getAttribute('data-poster');
    var video = document.createElement('video');

    video.className = 'c-ugc-testimonials__video';
    video.setAttribute('controls', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('preload', 'auto');
    // Keeps the frame on screen while the file buffers instead of flashing to
    // black — these are large files on a slow origin.
    if (poster) video.setAttribute('poster', poster);
    video.src = src;

    video.addEventListener('error', function () {
      card.innerHTML =
        '<p class="c-ugc-testimonials__state">This video cannot be played in your browser.</p>';
    });

    // The name bar and play button would sit over the player's own controls.
    card.innerHTML = '';
    card.appendChild(video);
    video.play().catch(function () {
      /* autoplay refused — the user still has the controls */
    });
  }

  function pauseAll(track) {
    var videos = track.querySelectorAll('video');
    Array.prototype.forEach.call(videos, function (video) {
      video.pause();
    });
  }

  /* --------------------------------------------------------------- carousel */

  function buildSwiper(root, track) {
    var container = root.querySelector('[data-ugc-swiper]');
    // The controls row is a sibling of the carousel, not a descendant. Walk up
    // rather than building a `#id` selector: Shopify section ids can start with
    // a digit, which is not a valid CSS id selector without escaping.
    var scope = root.closest('.c-ugc-testimonials') || document;
    var controls = scope.querySelector('[data-ugc-controls]');
    if (!container || typeof window.Swiper !== 'function') return null;

    var prev = controls && controls.querySelector('[data-ugc-prev]');
    var next = controls && controls.querySelector('[data-ugc-next]');
    var pagination = controls && controls.querySelector('[data-ugc-pagination]');

    if (controls) controls.hidden = false;

    return new window.Swiper(container, {
      slidesPerView: 1.25,
      spaceBetween: 12,
      grabCursor: true,
      watchOverflow: true,
      a11y: { enabled: true },
      navigation: { prevEl: prev, nextEl: next },
      pagination: { el: pagination, clickable: true },
      breakpoints: {
        768: { slidesPerView: 2.4, spaceBetween: 16 },
        978: { slidesPerView: 3, spaceBetween: 18 },
        1200: { slidesPerView: 4, spaceBetween: 18 },
      },
      on: {
        slideChange: function () {
          pauseAll(track);
        },
        // watchOverflow disables navigation when everything already fits; hide
        // the whole row rather than leaving dead controls on screen.
        lock: function () {
          if (controls) controls.classList.add('swiper-controls--hidden');
        },
        unlock: function () {
          if (controls) controls.classList.remove('swiper-controls--hidden');
        },
      },
    });
  }

  /* ----------------------------------------------------------------- render */

  /**
   * Object.assign copies keys whose value is `undefined`, which would clobber
   * the defaults whenever a theme setting is left blank. Drop them first.
   */
  function withDefaults(options) {
    var config = Object.assign({}, DEFAULTS);
    Object.keys(options || {}).forEach(function (key) {
      if (options[key] !== undefined && options[key] !== null && options[key] !== '') {
        config[key] = options[key];
      }
    });
    return config;
  }

  function showState(track, message) {
    track.innerHTML =
      '<div class="c-ugc-testimonials__slide swiper-slide">' +
      '<p class="c-ugc-testimonials__state">' +
      message +
      '</p>' +
      '</div>';
  }

  function render(root, options) {
    var config = withDefaults(options);
    var track = root.querySelector('.c-ugc-testimonials__track');
    if (!track) return;

    loadPosters(root);
    track.innerHTML = skeletonsHTML(4);

    var url = config.endpoint.replace(/\/$/, '') + '/api/testimonials';

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var raw = (data && data.testimonials) || [];

        var eligible = raw.filter(function (entry) {
          if (config.type !== 'all' && entry.type !== config.type) return false;
          // A `video`/`selfie` entry without media would render an empty card.
          if (entry.type !== 'review' && !entry.mediaUrl) return false;
          // A `review` with neither text nor media has nothing to show.
          if (entry.type === 'review' && !entry.text && !entry.mediaUrl) return false;
          return true;
        });

        if (config.type === 'all') eligible = onePerPerson(eligible);

        var items = interleave(mediaFirst(eligible).map(toViewModel), parseIds(config.demote));

        return dropUnplayableVideos(items);
      })
      .then(function (items) {
        if (!items.length) {
          showState(track, 'No testimonials to show yet.');
          return;
        }

        var slides = takeSlides(items, config.limit, parseIds(config.demote));

        track.innerHTML = slides
          .map(function (item) {
            return slideHTML(root, item);
          })
          .join('');

        buildSwiper(root, track);

        track.addEventListener('click', function (event) {
          var button = event.target.closest('.c-ugc-testimonials__play');
          if (button) activateVideo(button);
        });
      })
      .catch(function (error) {
        showState(track, 'Testimonials are temporarily unavailable.');
        if (window.console) console.error('[c-ugc-testimonials]', error);
      });
  }

  function init(root) {
    if (root.getAttribute('data-ugc-init') === '1') return;
    root.setAttribute('data-ugc-init', '1');

    render(root, {
      endpoint: root.getAttribute('data-endpoint') || undefined,
      type: root.getAttribute('data-type') || undefined,
      limit: parseInt(root.getAttribute('data-limit'), 10) || undefined,
      demote: root.getAttribute('data-demote') || undefined,
    });
  }

  function autoInit() {
    var roots = document.querySelectorAll('[data-ugc-testimonials]');
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }

  // Theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-ugc-testimonials]');
    if (root) init(root);
  });
})();
